import Sentry from "@similarweb/sentry-logger";

import { hideToast, showSuccessToast, showToast } from "app/actions/toast_actions";
import type { ExcelDownloadResult } from "app/components/React/ExcelButton/ExcelClientDownload";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { renderLoadingToast } from "app/pages/sales-intelligence/common-components/LoadingToast/LoadingToast";
import { createRevealExportService } from "app/pages/sales-intelligence/common-components/table-actions-panel/services/revealExportService";
import { selectContactsRequestDto } from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import type { ContactsDataSource } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext.types";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";
import type { GetRequestOptions, PostConfigObject } from "app/services/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkActionCommon, ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { selectRevealedContactsById } from "../../common/store/selectors";
import type { Integration } from "../../management/types";
import { exportToExcelToastThunk } from "../../table-actions-panel/effects";
import { exportContactsToExcelService } from "../../table-actions-panel/services/exportExcelService";
import { NEW_ACCOUNT_VALUE } from "../constants/export";
import { fixLinkedInUrl } from "../helpers/common";
import { createAllRevealedContactsExportDto, createContactsExportRequestDto } from "../helpers/dto";
import { EXPORT_SUCCESS_TAG } from "../helpers/exportFlow";
import trackingService from "../services/tracking/trackingService";
import type { ContactExportPreview, ContactSearchEntry, ContactSearchEntryWithKey, CRMEntity } from "../types/common";
import type {
    ContactIdCompanyPair,
    ContactMapping,
    ContactsExportRequestDto,
    ContactsSearchResponseDto,
    ContactsSearchResultsRequestDto,
    ExportContactsToCrmRequestDto,
    ExportContactsToCrmResponseDto,
} from "../types/dto";
import type { PendingRevealInfo } from "../types/exportFlow";

import * as ac from "./action-creators";
import { tableRowsSelectionChangedAction } from "./action-creators";
import { showExportContactsToCrmFailureThunk, showExportContactsToCrmFinishedThunk } from "./effect-utils-messages";
import {
    selectCanCreateNewAccount,
    selectContactsCost,
    selectContactsCrmExportLimit,
    selectContactSearchResultsDto,
    selectCrmToExportContacts,
    selectEntityToExportContactsAs,
    selectExportContactsToCrmMapping,
    selectExportMessageTranslationKeys,
    selectPendingRevealInfo,
    selectRequestedContactsForExport,
} from "./selectors";

export function fetchContactsSearchResultsWithTotalCountThunk(
    dto: ContactsSearchResultsRequestDto,
    config?: PostConfigObject,
) {
    return async function fetchContactsSearchResults(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.contactsSearchResultsWithTotalCountFetchedAsync.request());

        try {
            const response = await deps.si.api.contacts.fetchContactsDataWithTotalCount(dto, config);

            return dispatch(
                ac.contactsSearchResultsWithTotalCountFetchedAsync.success({
                    ...response,
                    rows: response.rows.map(fixLinkedInUrl),
                }),
            );
        } catch (e) {
            if (e?.name !== "AbortError") {
                return dispatch(ac.contactsSearchResultsWithTotalCountFetchedAsync.failure(e));
            }
        }
    };
}

export const makeFetchTopNContactsThunk = <Args extends unknown[]>(
    fetchContacts: (topN: number, ...args: Args) => ThunkActionCommon<Promise<ContactsSearchResponseDto>>,
) => {
    return (topN: number, ...args: Args) => {
        return async (dispatch: ThunkDispatchCommon): Promise<ContactSearchEntry[]> => {
            try {
                const response = await dispatch(fetchContacts(topN, ...args));
                return response.rows;
            } catch (error) {
                if (error?.name !== "AbortError") {
                    Sentry.errorTracker({
                        message: "makeFetchTopNContactsThunk error",
                        e: error,
                        level: "error",
                    });
                }

                return [];
            }
        };
    };
};

const getFetchContactsFromCompanyAnalysisThunk = (topN: number) => {
    return async (
        _dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<ContactsSearchResponseDto> => {
        const params = proNavigator.getParams();
        const dto = selectContactsRequestDto(getState(), params.key);

        const response = await deps.si.api.contacts.fetchContactsDataWithTotalCount({
            ...dto,
            page: 0,
            pageSize: topN,
        });
        return response;
    };
};

export const fetchTopNContactsFromCompanyAnalysisThunk = makeFetchTopNContactsThunk(
    getFetchContactsFromCompanyAnalysisThunk,
);

/**
 * @deprecated Use `useContactsQuotaQuery` hook instead.
 * This thunk is kept for legacy Redux integration only.
 */
export function fetchContactsUserQuotaThunk() {
    return async function fetchContactsUserQuota(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.userContactsQuotaFetchedAsync.request());

        try {
            if (!deps.si.settingsHelper.isDataCreditsEnabled) {
                const quota = await deps.si.api.quota.fetchContactsQuota();

                return dispatch(ac.userContactsQuotaFetchedAsync.success({ quota }));
            }

            const [quota, cost] = await Promise.all([
                quotaQueryClient.getContactsQuota(),
                quotaQueryClient.getContactCost(),
            ]);

            return dispatch(ac.userContactsQuotaFetchedAsync.success({ quota, cost }));
        } catch (e) {
            return dispatch(ac.userContactsQuotaFetchedAsync.failure(e));
        }
    };
}

export type ExcelExportResult = { success: boolean; exportedCount: number };

/**
 * API call to download an excel file based on given `dto`
 * @param exportExcel - Page-specific exporter resolved from ContactsFlowStrategy by the React dispatcher
 * @param isSilent - If true, skip showing result toast (for combined reveal+export flow)
 */
function exportContactsToExcelThunk(
    dto: ContactsExportRequestDto,
    exportExcel: ContactsDataSource["exportExcel"],
    isSilent?: boolean,
): ThunkActionCommon<Promise<ExcelExportResult>> {
    return async function exportContactsToExcel(dispatch: ThunkDispatchCommon) {
        dispatch(ac.exportedToExcelAsync.request());
        const { createToast, getExportingToastTitle, getSuccessToastTitle, getErrorToastTitle, getToastComponent } =
            exportContactsToExcelService;
        const requestedCount = dto.top || dto.includeContacts?.length || 0;
        const toast = createToast(getExportingToastTitle(requestedCount));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        try {
            const result: ExcelDownloadResult = await exportExcel(dto);
            dispatch(hideToast(toast));
            if (result?.success) {
                if (!isSilent) {
                    dispatch(exportToExcelToastThunk(getSuccessToastTitle(), EXPORT_SUCCESS_TAG));
                }
                dispatch(ac.exportedToExcelAsync.success());
                // Excel export is all-or-nothing: success means all requested contacts exported
                return { success: true, exportedCount: requestedCount };
            }
            if (!isSilent) {
                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            }
            dispatch(ac.exportedToExcelAsync.failure(result?.error?.message));
            return { success: false, exportedCount: 0 };
        } catch (e) {
            dispatch(hideToast(toast));
            if (!isSilent) {
                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            }
            dispatch(ac.exportedToExcelAsync.failure(e?.message));
            return { success: false, exportedCount: 0 };
        }
    };
}

export function exportSelectedContactsToExcelForLeadsSearchThunk(
    topNOrPairs: number | readonly ContactIdCompanyPair[],
    exportExcel: ContactsDataSource["exportExcel"],
    isSilent?: boolean,
): ThunkActionCommon<Promise<ExcelExportResult>> {
    return async function exportSelectedContactsToExcel(dispatch: ThunkDispatchCommon, getState: ThunkGetState) {
        const state = getState();

        const searchRequestDto = selectContactSearchResultsDto(state);

        const dto = createContactsExportRequestDto({
            searchRequestDto,
            topNOrPairs,
        });

        return dispatch(exportContactsToExcelThunk(dto, exportExcel, isSilent));
    };
}

export function exportSelectedContactsToExcelForCompanyThunk(
    topNOrPairs: number | readonly ContactIdCompanyPair[],
    domain: string,
    exportExcel: ContactsDataSource["exportExcel"],
    isSilent?: boolean,
) {
    return async function exportSelectedContactsToExcel(dispatch: ThunkDispatchCommon, getState: ThunkGetState) {
        const state = getState();

        const searchRequestDto = selectContactsRequestDto(state, domain);

        const dto = createContactsExportRequestDto({
            searchRequestDto,
            topNOrPairs,
        });

        return dispatch(exportContactsToExcelThunk(dto, exportExcel, isSilent));
    };
}

export function exportAllRevealedContactsForLeadsToExcelThunk(exportExcel: ContactsDataSource["exportExcel"]) {
    return async function exportAllRevealedContactsToExcel(dispatch: ThunkDispatchCommon) {
        const dto = createAllRevealedContactsExportDto();

        return dispatch(exportContactsToExcelThunk(dto, exportExcel));
    };
}

const prePopulateWithAccountId = (
    contacts: ContactExportPreview[],
    canCreateNewAccount: boolean,
): ContactExportPreview[] => {
    return contacts.map((contact) => {
        let accountId = undefined;

        if (contact.matchedCompanies.length === 0 && canCreateNewAccount) {
            accountId = NEW_ACCOUNT_VALUE;
        } else if (contact.matchedCompanies.length === 1) {
            accountId = contact.matchedCompanies[0].id;
        }

        return { ...contact, accountId };
    });
};

/**
 * Fetches CRM export preview for the given contact pairs.
 * Contacts must already be stored in requestedContactsForExport (populated by the export panel).
 */
export function fetchExportToCrmPreviewThunk(
    pairs: readonly ContactIdCompanyPair[],
    crm: Integration,
    options?: GetRequestOptions,
) {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(ac.fetchExportToCrmPreviewAsync.request());

        const requestedForExport = selectRequestedContactsForExport(getState());
        const matchPairs = (contact: ContactSearchEntry) =>
            pairs.some((pair) => pair.contactId === contact.id && pair.prospect === contact.site);

        let contacts = requestedForExport.filter(matchPairs);

        // Enrich with revealed data from Redux (needed after reveal+export flow)
        const revealedById = selectRevealedContactsById(getState());
        contacts = contacts.map((contact) => {
            if (contact.id in revealedById) {
                return { ...contact, isRevealed: true, privateData: revealedById[contact.id] };
            }
            return contact;
        });

        // CRM preview requires revealed contacts with private data
        contacts = contacts.filter((c) => c.isRevealed);

        if (contacts.length === 0) {
            dispatch(showExportContactsToCrmFailureThunk());
            return dispatch(ac.fetchExportToCrmPreviewAsync.failure());
        }

        const canCreateNewAccount = selectCanCreateNewAccount(getState());

        try {
            const contactsPreview = await deps.si.api.contactsSearch.fetchExportToCrmPreview(contacts, crm, options);

            const prePopulatedContacts = prePopulateWithAccountId(contactsPreview, canCreateNewAccount);
            return dispatch(ac.fetchExportToCrmPreviewAsync.success(prePopulatedContacts));
        } catch (error) {
            if (error?.name !== "AbortError") {
                dispatch(showExportContactsToCrmFailureThunk());
            }
            return dispatch(ac.fetchExportToCrmPreviewAsync.failure());
        }
    };
}

function makeExportContactsToCrmDto(
    exportAs: CRMEntity,
    countries: readonly number[],
    contacts: readonly ContactMapping[],
): ExportContactsToCrmRequestDto {
    if (exportAs === "contact") {
        return { contacts, countries };
    }

    return { leads: contacts, countries };
}

function exportContactsToCrmThunk(countries: readonly number[], contacts: readonly ContactMapping[]) {
    return async function exportContactsToCrm(
        _: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<ExportContactsToCrmResponseDto | undefined> {
        const state = getState();
        const crm = selectCrmToExportContacts(state);
        const as = selectEntityToExportContactsAs(state);
        if (!as || !crm) {
            return;
        }

        const dto = makeExportContactsToCrmDto(as, countries, contacts);

        try {
            const response = await deps.si.api.contactsSearch.exportContactsToCrm(dto, crm, as);
            return response;
        } catch (error) {
            if (error?.name !== "AbortError") {
                console.error(error);
            }
        }
    };
}

function showExportingContactsToCrmToastThunk(contactsCount: number) {
    return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const { crmName, keyBase } = selectExportMessageTranslationKeys(getState());
        const replacements = { contactsCount, crmName };
        const key = createPluralHelperForI18nKey(`${keyBase}.in_progress`)(contactsCount);
        const toastContent = renderLoadingToast({ content: translate(key, replacements) });
        return dispatch(showSuccessToast(toastContent, Date.now(), 0));
    };
}

export type CrmExportResult = { exportedCount: number; contacts: number; exportHistoryUrl?: string };

export function exportContactsToCrmAsLeadsThunk(
    pairs: readonly ContactIdCompanyPair[],
    countries: readonly number[],
    isSilent?: boolean,
): ThunkActionCommon<Promise<CrmExportResult>> {
    return async function exportContactsToCrmAsLeads(dispatch: ThunkDispatchCommon) {
        const { toast: exportingToast } = dispatch(showExportingContactsToCrmToastThunk(pairs.length));

        const contacts: ContactMapping[] = pairs.map((pair) => ({
            contactId: pair.contactId,
            domain: pair.prospect,
        }));

        if (contacts.length === 0) {
            dispatch(hideToast(exportingToast));
            if (!isSilent) {
                dispatch(showExportContactsToCrmFailureThunk());
            }
            return { exportedCount: 0, contacts: 0 };
        }

        const response = await dispatch(exportContactsToCrmThunk(countries, contacts));

        dispatch(hideToast(exportingToast));
        if (!isSilent) {
            dispatch(showExportContactsToCrmFinishedThunk({ response, contactsCount: pairs.length }));
        }
        return {
            exportedCount: response?.processedRecords || 0,
            contacts: contacts.length,
            exportHistoryUrl: response?.exportHistoryUrl,
        };
    };
}

export function showCombinedRevealExportToast(
    revealInfo: PendingRevealInfo,
    exportedCount: number,
    totalContacts: number,
    exportHistoryUrl?: string,
): ThunkActionCommon {
    return (dispatch) => {
        const { revealedCount, unrevealedCount, requestedCount, isFullRevealSuccess } = revealInfo;
        const isFullExportSuccess = exportedCount === totalContacts;
        const revealExportService = createRevealExportService();
        const isFullSuccess = isFullRevealSuccess && isFullExportSuccess;
        const messageContent = revealExportService.getRevealAndExportToastElement({
            revealedCount,
            unrevealedCount,
            requestedCount,
            exportedCount,
            isFullSuccess,
        });

        const toastContent = exportHistoryUrl
            ? getToastItemComponent({
                  text: messageContent,
                  href: exportHistoryUrl,
                  linkText: translate("si.management.sf.settings_export.toast.log"),
              })
            : messageContent;

        if (isFullSuccess) {
            dispatch(showSuccessToast(toastContent, undefined, undefined, undefined, EXPORT_SUCCESS_TAG));
        } else {
            dispatch(showToast(toastContent, "success", undefined));
        }

        const revealStatus = isFullRevealSuccess ? "success" : revealedCount > 0 ? "partial" : "failure";
        const exportStatus = isFullExportSuccess ? "success" : exportedCount > 0 ? "partial" : "failure";
        trackingService.trackSubmitRevealAndExport(revealStatus, exportStatus);

        dispatch(ac.clearPendingRevealInfo());
    };
}

export function exportContactsToCrmAsContactsThunk(
    countries: readonly number[],
    isSilent?: boolean,
): ThunkActionCommon<Promise<CrmExportResult>> {
    return async function exportContactsAsContactsToCrm(dispatch: ThunkDispatchCommon, getState: ThunkGetState) {
        const pendingRevealInfo = selectPendingRevealInfo(getState());

        const contacts = selectExportContactsToCrmMapping(getState());

        if (contacts.length === 0) {
            if (pendingRevealInfo) {
                dispatch(showCombinedRevealExportToast(pendingRevealInfo, 0, 0));
            } else if (!isSilent) {
                dispatch(showExportContactsToCrmFailureThunk());
            }
            return { exportedCount: 0, contacts: 0 };
        }

        const { toast: exportingToast } = dispatch(showExportingContactsToCrmToastThunk(contacts.length));

        const response = await dispatch(exportContactsToCrmThunk(countries, contacts));

        dispatch(hideToast(exportingToast));

        const exportedCount = response?.processedRecords || 0;
        const exportHistoryUrl = response?.exportHistoryUrl;

        if (pendingRevealInfo) {
            dispatch(
                showCombinedRevealExportToast(pendingRevealInfo, exportedCount, contacts.length, exportHistoryUrl),
            );
        } else if (!isSilent) {
            dispatch(
                showExportContactsToCrmFinishedThunk({
                    response,
                    contactsCount: contacts.length,
                }),
            );
        }

        return { exportedCount, contacts: contacts.length, exportHistoryUrl };
    };
}

export const setSelectedContactEntries =
    (selectedEntries: ContactSearchEntryWithKey[]) => (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const state = getState();

        const limit = selectContactsCrmExportLimit(state);

        let nextSelected = selectedEntries;

        if (limit < selectedEntries.length) {
            nextSelected = selectedEntries.slice(0, limit);
        }

        dispatch(tableRowsSelectionChangedAction(nextSelected));
    };

export const updateUsedContactsQuota =
    (usedContactsCount: number) =>
    (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        const cost = selectContactsCost(getState());

        const usedQuota = usedContactsCount * cost;

        if (deps.si.settingsHelper.isDataCreditsEnabled) {
            void quotaQueryClient.invalidateDataCreditsState();
        }

        dispatch(ac.usedQuotaUpdatedAction(usedQuota));
    };
